import {
  BASE_COLUMNS,
  COLUMN_BREAKPOINTS,
  GRID_GAP,
  SIZE_SPANS,
  STACK_COLUMN_THRESHOLD,
  WIDGET_SIZE_ORDER,
} from "./grid.constants";
import type {
  IWidgetDefinition,
  IWidgetPlacement,
  IWidgetSpan,
  TWidgetSizeKey,
} from "./types";

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/** Columns the canvas shows at this width. */
export function resolveColumns(width: number): number {
  const match = COLUMN_BREAKPOINTS.find((bp) => width >= bp.minWidth);
  return match?.columns ?? BASE_COLUMNS;
}

/** Width of one column in px, gaps excluded. */
export function resolveCellWidth(width: number, columns: number): number {
  if (width <= 0 || columns <= 0) return 0;
  return (width - GRID_GAP * (columns - 1)) / columns;
}

/**
 * Project a 12-column footprint onto the current column count, keeping the
 * widget's *proportion* of the row rather than its absolute cell count — a
 * half-width widget stays half-width at every breakpoint. Below the stack
 * threshold everything goes full width.
 */
export function scaleSpan(span: IWidgetSpan, columns: number): IWidgetSpan {
  if (columns <= STACK_COLUMN_THRESHOLD) {
    return { w: columns, h: span.h };
  }
  const scaled = Math.round((span.w * columns) / BASE_COLUMNS);
  return { w: clamp(scaled, 1, columns), h: span.h };
}

/** The footprint a placement occupies at the current column count. */
export function spanForSize(
  size: TWidgetSizeKey,
  columns: number,
): IWidgetSpan {
  return scaleSpan(SIZE_SPANS[size], columns);
}

/** Sizes this widget allows, in ascending order and with unknown keys dropped. */
export function allowedSizes(definition: IWidgetDefinition): TWidgetSizeKey[] {
  const allowed = WIDGET_SIZE_ORDER.filter((size) =>
    definition.sizes.includes(size),
  );
  return allowed.length > 0 ? allowed : [definition.defaultSize];
}

/** Coerce any size value onto the widget's allowed scale. */
export function normalizeSize(
  definition: IWidgetDefinition,
  size: string,
): TWidgetSizeKey {
  const allowed = allowedSizes(definition);
  return allowed.includes(size as TWidgetSizeKey)
    ? (size as TWidgetSizeKey)
    : allowed.includes(definition.defaultSize)
      ? definition.defaultSize
      : allowed[0];
}

/**
 * Step one notch along the widget's allowed scale. Returns the same size at
 * either end, so the caller can disable the control instead of wrapping around —
 * a stepper that silently jumps from largest back to smallest reads as a bug.
 */
export function stepSize(
  definition: IWidgetDefinition,
  current: TWidgetSizeKey,
  direction: 1 | -1,
): TWidgetSizeKey {
  const allowed = allowedSizes(definition);
  const index = allowed.indexOf(normalizeSize(definition, current));
  return allowed[clamp(index + direction, 0, allowed.length - 1)];
}

export function isMinSize(
  definition: IWidgetDefinition,
  size: TWidgetSizeKey,
): boolean {
  const allowed = allowedSizes(definition);
  return normalizeSize(definition, size) === allowed[0];
}

export function isMaxSize(
  definition: IWidgetDefinition,
  size: TWidgetSizeKey,
): boolean {
  const allowed = allowedSizes(definition);
  return normalizeSize(definition, size) === allowed[allowed.length - 1];
}

/**
 * Pick the allowed size whose footprint sits closest to a dragged one. Width is
 * weighted over height because the pointer has far more horizontal room to move
 * — without the weighting a mostly-sideways drag keeps snapping on height.
 */
export function nearestSize(
  definition: IWidgetDefinition,
  target: IWidgetSpan,
  columns: number,
): TWidgetSizeKey {
  const allowed = allowedSizes(definition);
  let best = allowed[0];
  let bestCost = Number.POSITIVE_INFINITY;

  for (const size of allowed) {
    const span = spanForSize(size, columns);
    const cost =
      Math.abs(span.w - target.w) * 1.35 + Math.abs(span.h - target.h);
    if (cost < bestCost) {
      bestCost = cost;
      best = size;
    }
  }
  return best;
}

/** Ids must satisfy the API's `^[A-Za-z0-9_-]+$` rule. */
export function makeInstanceId(type: string): string {
  const suffix =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().replace(/-/g, "").slice(0, 10)
      : Math.random().toString(36).slice(2, 12);
  return `${type}-${suffix}`;
}

/**
 * Make a stored layout structurally safe:
 *
 * - a widget type no longer in the registry is dropped (an older client version
 *   saved it, or the widget was retired)
 * - a size outside the widget's scale is pulled back onto it
 * - duplicate instance ids and extra copies of a singleton are dropped
 *
 * Permissions are deliberately NOT applied here — see `filterByPermission`.
 */
export function sanitizePlacements(
  placements: IWidgetPlacement[],
  registry: Map<string, IWidgetDefinition>,
): IWidgetPlacement[] {
  const seenIds = new Set<string>();
  const seenSingletons = new Set<string>();
  const result: IWidgetPlacement[] = [];

  for (const placement of placements) {
    const definition = registry.get(placement.type);
    if (!definition) continue;
    if (seenIds.has(placement.instanceId)) continue;
    if (definition.singleton) {
      if (seenSingletons.has(definition.type)) continue;
      seenSingletons.add(definition.type);
    }

    seenIds.add(placement.instanceId);
    result.push({
      instanceId: placement.instanceId,
      type: placement.type,
      size: normalizeSize(definition, placement.size),
    });
  }

  return result;
}

/**
 * Drop what the caller may not see, for rendering only.
 *
 * Kept separate from `sanitizePlacements` on purpose: a widget hidden by a
 * permission change stays in the *saved* layout, so it reappears by itself if
 * the permission comes back. Filtering at save time would quietly delete it the
 * next time the user so much as reordered a card.
 *
 * Takes the resolved set of allowed types rather than a predicate, so the result
 * is memoisable — a callback prop would be a new identity on every render and
 * would defeat every `useMemo` downstream of it.
 */
export function filterByPermission(
  placements: IWidgetPlacement[],
  allowedTypes: ReadonlySet<string>,
): IWidgetPlacement[] {
  return placements.filter((placement) => allowedTypes.has(placement.type));
}

/** Order-sensitive comparison — used to skip no-op saves. */
export function placementsEqual(
  a: IWidgetPlacement[],
  b: IWidgetPlacement[],
): boolean {
  if (a.length !== b.length) return false;
  return a.every((item, index) => {
    const other = b[index];
    return (
      item.instanceId === other.instanceId &&
      item.type === other.type &&
      item.size === other.size
    );
  });
}

/** Move an item to a new index, returning a new array. */
export function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0) return items;
  const next = items.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}
